// src/hooks/useDemo.js

import { createContext, useContext, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "./useAuth";
import { demoUsers, demoPeptideUsers } from "../data/demoData";
import {
  registerDemoInterceptor,
  ejectDemoInterceptor,
  initDemoState,
  clearDemoState,
  setDemoRole as setInterceptorRole,
} from "../services/demoInterceptor";
import api from "../services/api";
import apiAdmin from "../services/apiAdmin";
import { getDefaultDemoRole, getDemoLensProductLines } from "../utils/productLineUtils";

const demoContext = createContext();

const ROLE_HOME_ROUTES = {
  provider: "/orders",
  salesDashboard: "/salesDashboard",
  manufacturer: "/ordersManagement",
  peptidesProvider: "/peptides?view=orders",
  peptidesManufacturer: "/peptides?view=orders",
  peptidesPatient: "/peptides?view=catalog",
  peptidesSalesDashboard: "/salesDashboard",
};

const ALL_DEMO_USERS = { ...demoUsers, ...demoPeptideUsers };

function useDemoProvider() {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoRole, setDemoRole] = useState("provider");
  // Real-org product lines, captured at enterDemo so the role switcher can
  // keep showing every bucket the user is actually entitled to even after
  // switching to a fake demo persona whose orgInfo has different (or absent)
  // productLines. Without this, switching into a DME role hides the Peptides
  // bucket — the DME `provider` demo persona has no productLines field, which
  // triggers the legacy DME-only fallback in getProductLineFlags.
  const [realProductLines, setRealProductLines] = useState(null);
  const savedAuthRef = useRef(null);
  const savedLocationRef = useRef(null);
  const navigate = useNavigate();
  const { auth, setAuth, isDemoRef, hasGroup } = useAuth();

  const enterDemo = useCallback(() => {
    // Save current auth and location
    savedAuthRef.current = auth ? JSON.parse(JSON.stringify(auth)) : null;
    savedLocationRef.current = window.location.pathname;
    // Capture the user's EFFECTIVE product lines (org ∩ user, supervisor-bypass)
    // rather than the org's raw capability — otherwise a peptides-only sales rep
    // on a dual-line org would see the DME demo bucket (and enter as a DME
    // provider), and vice versa. The captured lens persists across role swaps
    // because the fake demo persona's auth no longer has the real user/org.
    const lens = getDemoLensProductLines(auth, hasGroup);
    setRealProductLines(lens);

    // Initial demo role depends on the user's effective product lines —
    // peptides-only users enter as a peptide provider; everyone else as DME.
    const defaultRole = getDefaultDemoRole(lens);

    // Init demo state and interceptor
    initDemoState();
    registerDemoInterceptor(api.api, apiAdmin.api);
    setInterceptorRole(defaultRole);

    // Set demo flag on auth (prevents checkAuth from firing)
    if (isDemoRef) isDemoRef.current = true;

    // Build fake auth with user's theme preference
    const fakeAuth = JSON.parse(JSON.stringify(ALL_DEMO_USERS[defaultRole]));
    if (auth?.user?.custom?.themePreference) {
      fakeAuth.user.custom.themePreference = auth.user.custom.themePreference;
    }
    setAuth(fakeAuth);

    setDemoRole(defaultRole);
    setIsDemoMode(true);

    // Force re-mount by navigating away first, then to target
    // This ensures the Orders component re-fetches with the interceptor active
    const target = ROLE_HOME_ROUTES[defaultRole];
    if (window.location.pathname === target) {
      navigate("/", { replace: true });
      setTimeout(() => navigate(target, { replace: true }), 0);
    } else {
      navigate(target);
    }
  }, [auth, hasGroup, setAuth, isDemoRef, navigate]);

  const exitDemo = useCallback(() => {
    // Eject interceptors
    ejectDemoInterceptor(api.api, apiAdmin.api);
    clearDemoState();

    // Clear demo flag
    if (isDemoRef) isDemoRef.current = false;

    // Restore real auth
    if (savedAuthRef.current) {
      setAuth(savedAuthRef.current);
    }

    setIsDemoMode(false);
    setDemoRole("provider");
    setRealProductLines(null);

    // Navigate back — force re-mount if returning to same route
    const returnPath = savedLocationRef.current || "/";
    savedAuthRef.current = null;
    savedLocationRef.current = null;
    if (window.location.pathname === returnPath) {
      navigate("/", { replace: true });
      setTimeout(() => navigate(returnPath, { replace: true }), 0);
    } else {
      navigate(returnPath);
    }
  }, [setAuth, isDemoRef, navigate]);

  const switchRole = useCallback(
    (role) => {
      if (!isDemoMode || role === demoRole) return;

      setInterceptorRole(role);

      // Build fake auth for new role, preserve theme
      const fakeAuth = JSON.parse(JSON.stringify(ALL_DEMO_USERS[role]));
      if (savedAuthRef.current?.user?.custom?.themePreference) {
        fakeAuth.user.custom.themePreference = savedAuthRef.current.user.custom.themePreference;
      }
      setAuth(fakeAuth);

      setDemoRole(role);

      // Force re-mount if already on the target route
      const target = ROLE_HOME_ROUTES[role];
      if (window.location.pathname === target) {
        navigate("/", { replace: true });
        setTimeout(() => navigate(target, { replace: true }), 0);
      } else {
        navigate(target);
      }
    },
    [isDemoMode, demoRole, setAuth, navigate]
  );

  return {
    isDemoMode,
    demoRole,
    enterDemo,
    exitDemo,
    switchRole,
    realProductLines,
  };
}

export function ProvideDemo({ children }) {
  const values = useDemoProvider();
  return <demoContext.Provider value={values}>{children}</demoContext.Provider>;
}

export const useDemo = () => {
  return useContext(demoContext);
};
